if (!customElements.get('wls-predictive-search')) {
  class WLSPredictiveSearch extends WLSSearchForm {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        results: 'js-wls-predictive-search-results',
        dropdown: 'js-predictive-search-dropdown',
        searchForm: 'wls-search-form',
      };

      this.cachedResults = {};
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.isOpen = false;
      this.states = {
        isOpen: false,
        isLoading: false,
        isError: false,
      };
      this.boundPredictiveSearchCloseHandler = this.handlePredictiveSearchClose.bind(this);
    }

    get elements() {
      return {
        ...super.elements,
        results: this.querySelector(`.${this.classes.results}`),
        dropdown: this.querySelector(`.${this.classes.dropdown}`),
        wlsSearchForm: this.querySelector(`${this.classes.searchForm}`),
      };
    }

    connectedCallback() {
      this.setupEventListeners();
      this.addEventListener('wls:predictive-search:close', this.boundPredictiveSearchCloseHandler);
    }

    disconnectedCallback() {
      this.removeEventListener('wls:predictive-search:close', this.boundPredictiveSearchCloseHandler);
    }

    setupEventListeners() {
      const { input, form, resetButton } = this.elements;
      input.addEventListener('input', debounce(this.onInput.bind(this), 300));
      input.addEventListener('focus', this.onFocus.bind(this));
      input.addEventListener('keydown', this.onKeydown.bind(this));
      form.addEventListener('submit', this.onFormSubmit.bind(this));
      resetButton.addEventListener('click', this.closeResults.bind(this));
      document.addEventListener('mousedown', this.onDocumentClick.bind(this));
    }

    getQuery() {
      const { input } = this.elements;
      return input.value.trim();
    }

    onInput() {
      const query = this.getQuery();
      this.searchTerm = query;
      if (!query) {
        this.closeResults();
        return;
      }
      if (this.cachedResults[query]) {
        this.renderResults(this.cachedResults[query]);
        return;
      }
      this.fetchResults(query);
    }

    onFocus() {
      if (this.searchTerm && this.cachedResults[this.searchTerm]) {
        this.renderResults(this.cachedResults[this.searchTerm]);
      }
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      const { input, results } = this.elements;
      const items = results.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
      if (!items.length) return;
      const active = document.activeElement;
      let idx = Array.from(items).indexOf(active);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        idx = (idx + 1) % items.length;
        items[idx].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        idx = (idx - 1 + items.length) % items.length;
        items[idx].focus();
      } else if (e.key === 'Escape') {
        this.closeResults();
        this.removeLoading();
        input.blur();
      }
    }

    onFormSubmit(e) {
      if (!this.getQuery()) {
        e.preventDefault();
        return;
      }
      this.closeResults();
    }

    onDocumentClick(e) {
      if (!this.contains(e.target)) {
        this.closeResults();
      }
    }

    fetchResults(query) {
      this.setLoading();
      fetch(`/search/suggest?q=${encodeURIComponent(query)}&section_id=wls-predictive-search`, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const html =
            new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-wls-predictive-search')
              ?.innerHTML || '';
          this.cachedResults[query] = html;
          if (this.searchTerm === query) {
            this.renderResults(html);
          }
          this.removeLoading();
        })
        .catch(() => {
          this.renderResults('<div class="wls-search-form__error">Error loading results</div>');
          this.removeLoading();
        })
        .finally(() => {
          this.openResults();
        });
    }

    setLoading() {
      const { wlsSearchForm } = this.elements;
      this.states.isLoading = true;
      wlsSearchForm.setLoading();
    }

    removeLoading() {
      const { wlsSearchForm } = this.elements;
      this.states.isLoading = false;
      wlsSearchForm.removeLoading();
    }

    renderResults(html) {
      const { results } = this.elements;
      results.innerHTML = html;
      this.openResults();
      this.states.isOpen = true;
    }

    openResults() {
      const { dropdown } = this.elements;
      dropdown.classList.add(this.classes.active);
      this.states.isOpen = true;
    }

    closeResults() {
      const { dropdown, results } = this.elements;
      dropdown.classList.remove(this.classes.active);
      results.innerHTML = '';
      this.states.isOpen = false;
    }

    handlePredictiveSearchClose() {
      this.closeResults();
      this.removeLoading();
    }
  }
  customElements.define('wls-predictive-search', WLSPredictiveSearch);
}
